import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSecurityValidation } from '@/hooks/useSecurityValidation';
import { useSecurityEnhancements } from '@/hooks/useSecurityEnhancements';
import { useSecurityHeaders } from '@/hooks/useSecurityHeaders';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { SecurityCleanupService } from '@/components/security/SecurityCleanupService';
import { SecurityHeaders } from '@/components/security/SecurityHeaders';
import { SecurityAlertBanner } from '@/components/security/SecurityAlertBanner';

interface SecurityContextType {
  validateOperation: (
    operation: string,
    requiredRole?: 'user' | 'admin' | 'superadmin',
    rateLimit?: { endpoint: string; maxRequests: number; windowMinutes: number }
  ) => Promise<boolean>;
  isSecurityValid: boolean;
  securityWarnings: string[];
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

export const SecurityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const {
    validateCriticalOperation,
    validateUserDataIntegrity,
    logSecurityEvent
  } = useSecurityValidation();
  
  const {
    validateServerRole,
    validateSecureOperation,
    logSensitiveAccess
  } = useSecurityEnhancements();
  
  // Apply security headers and monitoring
  useSecurityHeaders();
  
  // Configure session timeout (15 minutes for regular users, 30 minutes for admins)
  useSessionTimeout(user?.user_metadata?.role === 'admin' ? 30 : 15);
  
  const [isSecurityValid, setIsSecurityValid] = useState(true);
  const [securityWarnings, setSecurityWarnings] = useState<string[]>([]);

  // Main validation function with enhanced security
  const validateOperation = async (
    operation: string,
    requiredRole: 'user' | 'admin' | 'superadmin' = 'user',
    rateLimit?: { endpoint: string; maxRequests: number; windowMinutes: number }
  ): Promise<boolean> => {
    try {
      // Use enhanced security validation first
      const enhancedValidation = await validateSecureOperation(operation, requiredRole);
      if (!enhancedValidation) {
        return false;
      }

      // Fallback to original validation for backward compatibility
      const validation = await validateCriticalOperation(operation, requiredRole, rateLimit);
      
      if (!validation.isValid) {
        // Show security error to user
        toast({
          variant: "destructive",
          title: "Security Validation Failed",
          description: validation.errors.join(', '),
        });
        
        // Log the failed attempt
        await logSecurityEvent(`failed_${operation}`, 'security_validation', operation, false, validation.errors.join(', '));
        
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Security validation error:', error);
      toast({
        variant: "destructive",
        title: "Security Error",
        description: "Unable to validate operation security",
      });
      return false;
    }
  };

  // Monitor security status
  useEffect(() => {
    if (!user) {
      setIsSecurityValid(true);
      setSecurityWarnings([]);
      return;
    }

    const checkSecurity = async () => {
      try {
        const validation = await validateUserDataIntegrity();
        setIsSecurityValid(validation.isValid);
        
        // Only show critical errors, not warnings (role mismatches are warnings now)
        const criticalIssues = validation.errors.filter(error => 
          !error.includes('Client-side role mismatch')
        );
        setSecurityWarnings(criticalIssues);
        
        if (!validation.isValid && criticalIssues.length > 0) {
          console.warn('Critical security issues detected:', criticalIssues);
        }
      } catch (error) {
        console.error('Security check failed:', error);
        setIsSecurityValid(false);
      }
    };

    // Initial check
    checkSecurity();
    
    // Periodic checks every 2 minutes
    const interval = setInterval(checkSecurity, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, validateUserDataIntegrity]);

  // Security header monitoring
  useEffect(() => {
    // Monitor for potential security threats
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        // Re-validate when page becomes visible (potential tab switching attacks)
        validateUserDataIntegrity().then(validation => {
          if (!validation.isValid) {
            console.warn('Security validation failed on page focus');
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, validateUserDataIntegrity]);

  const contextValue: SecurityContextType = {
    validateOperation,
    isSecurityValid,
    securityWarnings
  };

  return (
    <SecurityContext.Provider value={contextValue}>
      <SecurityHeaders />
      {/* SecurityAlertBanner temporarily disabled - causing false positives */}
      <SecurityCleanupService />
      {children}
    </SecurityContext.Provider>
  );
};